<script lang="ts">
  import { api } from '$shared/api';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { isLoggedIn } from '$lib/auth.svelte';

  interface Winner {
    rank: number;
    username: string;
    avatar_url: string | null;
    points: number;
  }

  interface Standing {
    id: number;
    username: string;
    avatar_url: string | null;
    points: number;
    rank: number;
  }

  interface ViewerFinish {
    rank: number;
    points: number;
  }

  interface WrapUp {
    season: { number: number; start_date: string; end_date: string };
    winners: Winner[];
    standings: Standing[];
    viewer: ViewerFinish | null;
  }

  let loading = $state(true);
  let wrapUp = $state<WrapUp | null>(null);

  const loggedIn = $derived(isLoggedIn());

  $effect(() => {
    api
      .get<{ wrap_up: WrapUp | null }>('/seasons/wrap-up')
      .then((res) => {
        wrapUp = res.wrap_up ?? null;
      })
      .catch((e) => console.warn('Season wrap-up load failed:', e))
      .finally(() => {
        loading = false;
      });
  });

  function rankDisplay(rank: number): string {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
</script>

{#if loading}
  <LoadingSkeleton lines={10} />
{:else if !wrapUp}
  <h1>Week Wrap-Up</h1>
  <div class="empty-state">
    <div class="empty-icon">🏁</div>
    <p>No completed week yet — check back after the first week ends.</p>
    <a href="#/leaderboard" class="back-link">View Current Leaderboard</a>
  </div>
{:else}
  <h1>Week {wrapUp.season.number} — Final Results</h1>
  <p class="date-range">{formatDate(wrapUp.season.start_date)} – {formatDate(wrapUp.season.end_date)}</p>

  {#if wrapUp.winners.length > 0}
    <section class="podium-section">
      <h2>Top Players</h2>
      <div class="podium">
        {#each wrapUp.winners as winner}
          <a href="#/users/{winner.username}" class="podium-card">
            <span class="podium-rank">{rankDisplay(winner.rank)}</span>
            <Avatar username={winner.username} avatarUrl={winner.avatar_url} size="sm" />
            <span class="podium-username">{winner.username}</span>
            <span class="podium-points">{winner.points.toLocaleString()} pts</span>
          </a>
        {/each}
      </div>
    </section>
  {/if}

  {#if loggedIn && wrapUp.viewer}
    <section class="viewer-section">
      <h2>Your Finish</h2>
      <div class="viewer-card">
        <span class="viewer-rank">{rankDisplay(wrapUp.viewer.rank)}</span>
        <span class="viewer-pts">{wrapUp.viewer.points.toLocaleString()} pts</span>
      </div>
    </section>
  {:else if loggedIn}
    <section class="viewer-section">
      <p class="unranked-note">You didn't score any points this week — no rank recorded.</p>
    </section>
  {/if}

  {#if wrapUp.standings.length > 0}
    <section class="standings-section">
      <h2>Full Standings</h2>
      <div class="lb-table">
        <div class="lb-header">
          <span class="lb-col-rank">Rank</span>
          <span class="lb-col-player">Player</span>
          <span class="lb-col-value">Points</span>
        </div>
        {#each wrapUp.standings as entry}
          <a href="#/users/{entry.username}" class="lb-row">
            <span class="lb-col-rank">{rankDisplay(entry.rank)}</span>
            <span class="lb-col-player">
              <Avatar username={entry.username} avatarUrl={entry.avatar_url} size="xs" />
              <span class="lb-username">{entry.username}</span>
            </span>
            <span class="lb-col-value lb-value">{entry.points.toLocaleString()}</span>
          </a>
        {/each}
      </div>
    </section>
  {/if}

  <div class="nav-links">
    <a href="#/leaderboard" class="back-link">View Current Leaderboard</a>
  </div>
{/if}

<style>
  h1 {
    margin-bottom: 0.25rem;
  }

  .date-range {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin-bottom: 2rem;
  }

  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--text-muted);
  }
  .empty-icon {
    font-size: 3rem;
    margin-bottom: 0.75rem;
  }

  .podium-section,
  .viewer-section,
  .standings-section {
    margin-bottom: 2rem;
  }
  .podium-section h2,
  .viewer-section h2,
  .standings-section h2 {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  .podium {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .podium-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    padding: 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    text-decoration: none;
    color: var(--text);
    min-width: 100px;
    transition: background 0.15s;
  }
  .podium-card:hover {
    background: var(--surface-2);
  }
  .podium-rank {
    font-size: 1.5rem;
  }
  .podium-username {
    font-weight: 600;
    font-size: 0.85rem;
  }
  .podium-points {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .viewer-card {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1.25rem;
    background: rgba(0, 229, 255, 0.08);
    border: 1px solid rgba(0, 229, 255, 0.25);
    border-radius: var(--radius);
  }
  .viewer-rank {
    font-size: 1.25rem;
    font-weight: 700;
  }
  .viewer-pts {
    font-size: 1rem;
    font-weight: 600;
    color: var(--cyan);
  }
  .unranked-note {
    font-size: 0.9rem;
    color: var(--text-muted);
  }

  .lb-table {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }
  .lb-header {
    display: grid;
    grid-template-columns: 50px 1fr 80px;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid var(--border);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    font-weight: 600;
  }
  .lb-row {
    display: grid;
    grid-template-columns: 50px 1fr 80px;
    padding: 0.5rem 1rem;
    align-items: center;
    border-bottom: 1px solid var(--border);
    text-decoration: none;
    transition: background 0.15s;
  }
  .lb-row:last-child {
    border-bottom: none;
  }
  .lb-row:hover {
    background: var(--surface-2);
  }
  .lb-col-rank {
    font-weight: 700;
    font-size: 0.85rem;
  }
  .lb-col-player {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }
  .lb-col-value {
    text-align: right;
  }
  .lb-username {
    font-weight: 500;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .lb-value {
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--cyan);
  }

  .nav-links {
    margin-top: 1.5rem;
  }
  .back-link {
    font-size: 0.85rem;
    color: var(--cyan);
    text-decoration: none;
  }
  .back-link:hover {
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    .lb-header,
    .lb-row {
      padding: 0.4rem 0.75rem;
    }
    .podium {
      gap: 0.5rem;
    }
    .podium-card {
      min-width: 80px;
      padding: 0.75rem;
    }
  }
</style>
